import useApi from "@/hooks/useApi";
import React, { useEffect, useRef, useState } from "react";
import { SecondaryBlueButton, PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { AIConfiguration } from "../types";

interface VersionSummary {
  id: number;
  configName: string;
  changedBy: string;
  changeType: "Created" | "Updated" | "Deleted" | "Imported";
  savedAt: string;
}

interface VersionDetail extends VersionSummary {
  configJson: string;
}

interface Props {
  config: AIConfiguration;
  canExportImport: boolean;   // gated by subscription.enableAdvancedAIFeatures
  close(): void;
  onRestore(partial: Partial<AIConfiguration>): void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatSavedAt(savedAt: string): string {
  const date = new Date(savedAt.replace(" UTC", "Z"));
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yestStart = new Date(todayStart);
  yestStart.setDate(yestStart.getDate() - 1);
  const versionDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (versionDay.getTime() === todayStart.getTime()) return `Today · ${timeStr}`;
  if (versionDay.getTime() === yestStart.getTime()) return `Yesterday · ${timeStr}`;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} · ${timeStr}`;
}

function shortEmail(email: string): string {
  if (!email) return "Unknown";
  return email.split("@")[0];
}

const CHANGE_TYPE_STYLES: Record<string, string> = {
  Created:  "bg-green-100 text-green-700",
  Updated:  "bg-blue-100 text-blue-700",
  Deleted:  "bg-red-100 text-red-700",
  Imported: "bg-purple-100 text-purple-700"
};

export default function AIConfigVersionHistoryModal({ config, canExportImport, close, onRestore }: Props) {
  const api = useApi();
  const [versions, setVersions] = useState<VersionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importPreview, setImportPreview] = useState<{ name: string; json: string } | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.admin.get(`AIConfigurationVersions/${config.id}`);
        if (res.ok) {
          const data = await res.json();
          setVersions(Array.isArray(data) ? data : []);
        }
      } catch {
        setError("Could not load version history.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [api.admin, config.id]);

  async function handleRestore(versionId: number) {
    setRestoring(true);
    setError(null);
    try {
      const res = await api.admin.get(`AIConfigurationVersions/${config.id}/${versionId}`);
      if (!res.ok) { setError("Could not load that version."); return; }
      const detail: VersionDetail = await res.json();
      const partial = JSON.parse(detail.configJson ?? "{}") as Partial<AIConfiguration>;
      // Always preserve the current id and indexName — never restore infra fields
      partial.id = config.id;
      partial.indexName = config.indexName;
      partial.azureDeploymentName = config.azureDeploymentName;
      onRestore(partial);
      close();
    } catch {
      setError("Could not restore that version.");
    } finally {
      setRestoring(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    setError(null);
    try {
      const res = await api.admin.get(`AIConfigurationVersions/${config.id}/export`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as any).error ?? "Export failed.");
        return;
      }
      const disposition = res.headers.get("content-disposition") ?? "";
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = match?.[1] ?? `bot-config-${config.id}.json`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Export failed.");
    } finally {
      setExporting(false);
    }
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = "";
    setError(null);

    if (file.size > 512 * 1024) {
      setError("File too large — Velaro bot config files should be under 512 KB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        setError("Invalid JSON — the file could not be parsed. Please export a fresh copy and try again.");
        return;
      }
      if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
        setError("Unexpected format — the file does not look like a Velaro bot config.");
        return;
      }
      const name = typeof parsed.name === "string" && parsed.name.trim()
        ? parsed.name.trim()
        : "Unnamed config";
      setImportPreview({ name, json: text });
    };
    reader.onerror = () => setError("Could not read the file.");
    reader.readAsText(file);
  }

  async function confirmImport() {
    if (!importPreview) return;
    setImporting(true);
    setError(null);
    try {
      const res = await api.admin.post(`AIConfigurationVersions/${config.id}/import`, { configJson: importPreview.json });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as any).error ?? "Import failed.");
        return;
      }
      const result = await res.json();
      const partial = JSON.parse(result.configJson ?? "{}") as Partial<AIConfiguration>;
      partial.id = config.id;
      partial.indexName = config.indexName;
      partial.azureDeploymentName = config.azureDeploymentName;
      onRestore(partial);
      close();
    } catch {
      setError("Import failed. Please try again.");
    } finally {
      setImporting(false);
      setImportPreview(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold text-slate-800">Version History</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {config.name} — last {versions.length} change{versions.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={close}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none"
            aria-label="Close version history"
          >
            ×
          </button>
        </div>

        {/* Export / Import toolbar — always shown but import gated by subscription */}
        <div className="flex items-center gap-2 px-5 py-2.5 border-b bg-slate-50">
          <button
            onClick={handleExport}
            disabled={exporting || versions.length === 0}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            {exporting ? "Exporting…" : "⬇ Export JSON"}
          </button>

          {canExportImport ? (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
              >
                {importing ? "Importing…" : "⬆ Import JSON"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleImportFile}
                aria-label="Import bot config JSON file"
              />
            </>
          ) : (
            <span className="text-xs text-slate-400 italic">
              Import available on Pro / Enterprise
            </span>
          )}
          <span className="ml-auto text-xs text-slate-400">
            Restoring pre-fills the editor — nothing saves until you click Save.
          </span>
        </div>

        {/* Import confirmation banner */}
        {importPreview && (
          <div className="mx-5 my-2 p-3 rounded-lg border border-amber-300 bg-amber-50">
            <p className="text-sm font-medium text-amber-800 mb-1">
              Import &ldquo;{importPreview.name}&rdquo;?
            </p>
            <p className="text-xs text-amber-700 mb-3">
              This will pre-fill the editor with the imported settings. Nothing changes until you click&nbsp;<strong>Save</strong>.
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirmImport}
                disabled={importing}
                className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50 transition-colors"
              >
                {importing ? "Importing…" : "Confirm import"}
              </button>
              <button
                onClick={() => setImportPreview(null)}
                className="text-xs px-3 py-1.5 rounded border border-amber-300 bg-white hover:bg-amber-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Version list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <p className="text-slate-500 text-sm py-6 text-center">Loading history…</p>
          )}
          {!loading && versions.length === 0 && (
            <p className="text-slate-500 text-sm py-6 text-center">
              No version history yet. Versions are saved automatically when you save this bot config.
            </p>
          )}
          {!loading && versions.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {versions.map((v, idx) => {
                const isCurrent = idx === 0 && v.changeType !== "Deleted";
                return (
                  <li
                    key={v.id}
                    className={`py-3 px-2 rounded transition-colors ${
                      isCurrent ? "cursor-default" : "cursor-pointer hover:bg-slate-50"
                    } ${selected === v.id ? "bg-primary-bg" : ""}`}
                    onClick={() => !isCurrent && setSelected(selected === v.id ? null : v.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          CHANGE_TYPE_STYLES[v.changeType] ?? "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {v.changeType}
                      </span>
                      <span className="text-sm font-semibold text-slate-800">
                        {formatSavedAt(v.savedAt)}
                      </span>
                      {isCurrent && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      {v.configName && (
                        <span className="text-xs text-slate-500 truncate max-w-[200px]">{v.configName}</span>
                      )}
                      {v.changedBy && (
                        <>
                          <span className="text-slate-200 text-xs">·</span>
                          <span className="text-xs text-slate-400">by {shortEmail(v.changedBy)}</span>
                        </>
                      )}
                    </div>

                    {selected === v.id && !isCurrent && (
                      <div className="mt-2 pt-2 border-t border-slate-100 flex flex-col gap-1.5">
                        <PrimaryBlueButton
                          label={restoring ? "Restoring…" : "Restore this version"}
                          onClick={(e) => { e.stopPropagation(); handleRestore(v.id); }}
                        />
                        <p className="text-xs text-slate-400">
                          Opens the editor pre-filled with this version. Your current settings are preserved until you click Save.
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t">
          <SecondaryBlueButton label="Close" onClick={close} />
        </div>
      </div>
    </div>
  );
}
